import { useEffect, useState } from 'react';
import { ActionIcon, Box, Group, Menu, Switch, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import {
  IconAlertCircle,
  IconCalendarCog,
  IconCalendarEvent,
  IconCalendarOff,
  IconCheck,
  IconDotsVertical,
  IconLink,
  IconLock,
  IconLockOpen,
  IconMessageCircle,
  IconShare,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { MealPlan } from '../../types/mealPlan';
import { formatDateRange, getExpectedDays } from '../../utils/mealPlanUtils';
import { buildPlanPath } from '../../utils/routeHelpers';
import { useAuth } from '../../hooks/useAuth';
import { useAnimatedDisclosure } from '../../hooks/useAnimatedDisclosure';
import {
  removeMealPlanFromCalendar,
  syncMealPlanToCalendar,
} from '../../services/calendarService';
import { MealPlanPreferencesDrawer } from '../MealPlanning/MealPlanPreferencesDrawer';
import { ContextualFeedbackModal } from '../Support/ContextualFeedbackModal';

interface MealPlanCardMenuProps {
  mealPlan: MealPlan;
  /** Plan owner, for the legacy /u/:userId link of plans without a shortId. */
  userId: string;
  /** Awaited, so the trigger can spin until the plan is actually gone. */
  onDeletePlan?: (plan: MealPlan) => void | Promise<void>;
  /** Refreshes the card's list after sharing or calendar state changes. */
  onPlanUpdated?: () => void;
  /** Sharing is only reachable once the user's profile is public. */
  profilePublic?: boolean;
  calendarConnected?: boolean;
  /** Draws the trigger white, for the dashboard's gradient hero card. */
  onGradient?: boolean;
}

/**
 * Kebab menu of plan-level actions for a meal plan card, carrying the items
 * the plan page keeps behind its own kebab so acting on a plan from the
 * dashboard or the history list no longer means opening it first. Viewing and
 * activating stay out — the cards carry their own buttons for both.
 *
 * The rest track what the plan can actually do: a plan still generating has
 * nothing to act on and renders no trigger at all, a failed one only offers
 * its preferences and delete, and calendar sync only an active plan.
 */
export const MealPlanCardMenu = ({
  mealPlan,
  userId,
  onDeletePlan,
  onPlanUpdated,
  profilePublic = false,
  calendarConnected = false,
  onGradient = false,
}: MealPlanCardMenuProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const { user } = useAuth();

  // Mounted on first use rather than with the card — a list renders one menu
  // per plan — but animated in and out like any other overlay.
  const preferences = useAnimatedDisclosure();
  const feedback = useAnimatedDisclosure();
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Sharing flips on the card before the request lands, and re-syncs whenever
  // the list refetches the plan.
  const [isShared, setIsShared] = useState(!!mealPlan.isShared);
  useEffect(() => setIsShared(!!mealPlan.isShared), [mealPlan.isShared]);

  const generationStatus = mealPlan.generationStatus.status;
  const isCompleted = generationStatus === 'completed';
  const isFailed = generationStatus === 'failed';
  const groceryListBusy =
    mealPlan.groceryListStatus?.status === 'pending' ||
    mealPlan.groceryListStatus?.status === 'generating';

  const showPreferences = !!mealPlan.preferences;
  const showSharing = isCompleted;
  const showCalendar =
    isCompleted && calendarConnected && mealPlan.status === 'active';
  const showFeedback = isCompleted;
  const showDelete = (isCompleted || isFailed) && !!onDeletePlan;

  const hasUpperItems = showPreferences || showSharing || showCalendar;

  if (!hasUpperItems && !showFeedback && !showDelete) {
    return null;
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}${buildPlanPath(mealPlan, userId)}`
      );

      notifications.show({
        title: 'Link copied',
        message: 'Meal plan link copied to clipboard',
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error copying link:', error);
      notifications.show({
        title: 'Copy failed',
        message: 'Failed to copy link to clipboard',
        color: 'red',
        icon: <IconX size={18} />,
        autoClose: 5000,
      });
    }
  };

  const handleToggleShare = async (nextShared: boolean) => {
    if (!user) return;

    const previous = isShared;
    setIsShared(nextShared);

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/meals/plans/${mealPlan.id}/share`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ isShared: nextShared }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update sharing status');
      }

      onPlanUpdated?.();
    } catch (error) {
      console.error('Error toggling meal plan sharing:', error);
      setIsShared(previous);
      notifications.show({
        title: 'Failed',
        message: 'Failed to update sharing status',
        color: 'red',
        icon: <IconX size={18} />,
        autoClose: 5000,
      });
    }
  };

  const handleSyncCalendar = async () => {
    if (!user) return;
    setLoadingCalendar(true);
    try {
      const token = await user.getIdToken();
      await syncMealPlanToCalendar(token, mealPlan.id);
      notifications.show({
        title: 'Synced to calendar',
        message: 'Your meal plan events have been synced to Google Calendar.',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
      onPlanUpdated?.();
    } catch (error: any) {
      notifications.show({
        title: 'Sync failed',
        message: error.message || 'Failed to sync to calendar. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleRemoveCalendar = async () => {
    if (!user) return;
    setLoadingCalendar(true);
    try {
      const token = await user.getIdToken();
      await removeMealPlanFromCalendar(token, mealPlan.id);
      notifications.show({
        title: 'Removed from calendar',
        message: 'Calendar events for this meal plan have been removed.',
        color: 'blue',
        icon: <IconCheck size={18} />,
      });
      onPlanUpdated?.();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message:
          error.message || 'Failed to remove calendar events. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setLoadingCalendar(false);
    }
  };

  // Deleting takes a couple of seconds and closes the menu on click, so the
  // trigger holds a spinner until the card is gone: without it the click
  // reads as having done nothing at all.
  const handleDelete = async () => {
    if (!onDeletePlan) return;
    setDeleting(true);
    try {
      await onDeletePlan(mealPlan);
    } finally {
      setDeleting(false);
    }
  };

  // Mobile dropdowns are touch targets, so the items grow the way the plan
  // page's own menu does at the same breakpoint.
  const itemStyle = isMobile
    ? { fontSize: '16px', padding: '12px 16px' }
    : undefined;
  const iconSize = isMobile ? 18 : 14;

  return (
    <>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            size="lg"
            c={onGradient ? 'white' : undefined}
            aria-label={deleting ? 'Deleting meal plan' : 'Meal plan actions'}
            loading={deleting}
            loaderProps={onGradient ? { color: 'white' } : undefined}
            // Cards put click handlers on their surroundings; opening the menu
            // should never also open the plan or a recipe.
            onClick={(event) => event.stopPropagation()}
          >
            <IconDotsVertical size={20} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
          {showPreferences && (
            <Menu.Item
              leftSection={<IconCalendarCog size={iconSize} stroke={1.5} />}
              onClick={preferences.open}
              style={itemStyle}
            >
              Meal plan preferences
            </Menu.Item>
          )}

          {showSharing && (
            <Menu.Item
              closeMenuOnClick={!profilePublic}
              onClick={(e) => {
                if (!profilePublic) {
                  // Profile not public - navigate to settings
                  navigate('/profile?section=privacy');
                } else {
                  // Only toggle if not clicking on the switch or copy button
                  const target = e.target as HTMLElement;
                  if (
                    !target.closest('[data-switch-wrapper]') &&
                    !target.closest('[data-copy-button]')
                  ) {
                    handleToggleShare(!isShared);
                  }
                }
              }}
              style={itemStyle}
            >
              <Group justify="space-between" wrap="nowrap" gap="md" w="100%">
                <Group gap="xs" style={{ flex: 1 }}>
                  {profilePublic ? (
                    isShared ? (
                      <IconLockOpen size={iconSize} stroke={1.5} />
                    ) : (
                      <IconLock size={iconSize} stroke={1.5} />
                    )
                  ) : (
                    <IconShare size={iconSize} stroke={1.5} />
                  )}
                  <Text size={isMobile ? 'md' : 'sm'}>
                    {profilePublic ? 'Sharing' : 'Enable sharing'}
                  </Text>
                  {profilePublic && (
                    <Box data-switch-wrapper>
                      <Switch
                        checked={isShared}
                        aria-label="Share meal plan"
                        onChange={(event) =>
                          handleToggleShare(event.currentTarget.checked)
                        }
                      />
                    </Box>
                  )}
                </Group>
                {profilePublic && (
                  <ActionIcon
                    component="div"
                    variant="subtle"
                    size="sm"
                    disabled={!isShared}
                    data-copy-button
                    aria-label="Copy meal plan link"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isShared) handleCopyLink();
                    }}
                    style={{ cursor: isShared ? 'pointer' : 'default' }}
                  >
                    <IconLink size={16} />
                  </ActionIcon>
                )}
              </Group>
            </Menu.Item>
          )}

          {showCalendar && (
            <Menu.Item
              leftSection={<IconCalendarEvent size={iconSize} stroke={1.5} />}
              onClick={handleSyncCalendar}
              disabled={loadingCalendar}
              style={itemStyle}
            >
              {loadingCalendar
                ? 'Syncing...'
                : mealPlan.calendarSyncedAt
                  ? 'Re-sync to calendar'
                  : 'Sync to calendar'}
            </Menu.Item>
          )}

          {showCalendar && mealPlan.calendarSyncedAt && (
            <Menu.Item
              leftSection={<IconCalendarOff size={iconSize} stroke={1.5} />}
              onClick={handleRemoveCalendar}
              disabled={loadingCalendar}
              style={itemStyle}
            >
              Remove from calendar
            </Menu.Item>
          )}

          {hasUpperItems && (showFeedback || showDelete) && <Menu.Divider />}

          {showFeedback && (
            <Menu.Item
              leftSection={<IconMessageCircle size={iconSize} stroke={1.5} />}
              onClick={feedback.open}
              style={itemStyle}
            >
              Send feedback
            </Menu.Item>
          )}

          {showDelete && (
            <Menu.Item
              leftSection={<IconTrash size={iconSize} stroke={1.5} />}
              color="red"
              onClick={handleDelete}
              disabled={groceryListBusy}
              style={itemStyle}
            >
              Delete meal plan
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>

      {preferences.mounted && (
        <MealPlanPreferencesDrawer
          opened={preferences.opened}
          onClose={preferences.close}
          preferences={mealPlan.preferences}
          profileSnapshot={mealPlan.profileSnapshot}
          totalDays={getExpectedDays(mealPlan).length}
          dateRange={[mealPlan.startDate, mealPlan.endDate]}
        />
      )}

      {feedback.mounted && (
        <ContextualFeedbackModal
          opened={feedback.opened}
          onClose={feedback.close}
          context={{
            type: 'meal_plan',
            mealPlanId: mealPlan.id,
            mealPlanDateRange: formatDateRange(mealPlan, true),
          }}
        />
      )}
    </>
  );
};

export default MealPlanCardMenu;
